// Ledger: the chart of accounts, and the journal page by page. The chart is the home's (the
// REGISTERED pack's accounts: registry data, no figures), grouped under its account types in the
// pack's order. The journal is the engine's browse: fifty entries a page, "Show more" continuing the
// same walk with the cursor it served.

import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button, Card, Icon, INSET_FOCUS_RING, TAB_LANE, cn } from '@givmo/design-system';
import type { BooksDimension, BooksHome, BooksJournalEntry } from './books-wire';
import type { BooksWords } from './books-words';
import { BooksLoading, BooksPageShell, BooksStateCard } from './books-ui';
import { BooksLink } from './books-links';
import { useBooksJournal, useShowMore, type BooksWalk } from './books-queries';
import { count, dateShort, moneyOrDash } from './books-format';
import { memberWords } from './statement-faces';

/** How a walk's order is said above its first page. */
export const WALK_WORDS: Record<BooksWalk, string> = { oldest_first: 'Oldest first' };

export function LedgerPage({
  home,
  words,
  tab,
}: {
  home: BooksHome;
  words: BooksWords;
  tab: 'accounts' | 'journal';
}) {
  return (
    <BooksPageShell title="Ledger" testId="books-ledger">
      <LedgerTabs current={tab} />
      {tab === 'accounts' ? (
        <ChartOfAccounts home={home} words={words} />
      ) : (
        <Journal home={home} words={words} walk="oldest_first" />
      )}
    </BooksPageShell>
  );
}

function LedgerTabs({ current }: { current: 'accounts' | 'journal' }) {
  const tabs = [
    { id: 'accounts', label: 'Chart of accounts', page: 'ledger' },
    { id: 'journal', label: 'Journal', page: 'journal' },
  ] as const;
  return (
    <nav aria-label="Ledger" data-testid="books-ledger-tabs">
      <ul className={cn('relative flex gap-24 border-b border-border', TAB_LANE)}>
        {tabs.map((tab) => (
          <li key={tab.id} className="relative shrink-0">
            <BooksLink
              view={{ page: tab.page }}
              aria-current={tab.id === current ? 'page' : undefined}
              className={cn(
                'relative block whitespace-nowrap rounded-t-md px-4 py-10 font-sans text-s14 font-w500 tracking-normal text-text-tertiary outline-none transition-colors hover:text-text aria-[current=page]:font-w600 aria-[current=page]:text-text-strong',
                INSET_FOCUS_RING,
              )}
              data-testid={`books-ledger-tab-${tab.id}`}
            >
              {tab.label}
              {tab.id === current ? (
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-2 rounded-full bg-brand-600"
                />
              ) : null}
            </BooksLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

const HEAD =
  'h-[40px] px-16 font-sans text-s11 font-w600 uppercase tracking-wide text-text-secondary';

function ChartOfAccounts({ home, words }: { home: BooksHome; words: BooksWords }) {
  const types: string[] = [];
  for (const account of home.chart) {
    if (!types.includes(account.account_type)) types.push(account.account_type);
  }
  return (
    <Card testId="books-chart">
      <table className="w-full border-collapse text-left">
        <colgroup>
          <col style={{ width: '96px' }} />
          <col />
          <col style={{ width: '320px' }} />
          <col style={{ width: '120px' }} />
        </colgroup>
        <thead className="bg-neutral-50">
          <tr className="border-b border-border-subtle">
            <th className={HEAD}>Code</th>
            <th className={HEAD}>Account</th>
            <th className={HEAD}>Section</th>
            <th className="h-[40px] px-16" aria-label="Activity" />
          </tr>
        </thead>
        {types.map((type) => (
          <tbody key={type}>
            <tr className="border-b border-border-subtle">
              <th
                colSpan={4}
                scope="rowgroup"
                className="h-[40px] px-16 pt-10 text-left align-bottom font-sans text-s13 font-w600 tracking-givmo text-text-strong"
              >
                {words.accountType(type)}
              </th>
            </tr>
            {home.chart
              .filter((account) => account.account_type === type)
              .map((account) => (
                <tr
                  key={account.account_code}
                  className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover"
                  data-testid={`books-chart-${account.account_code}`}
                >
                  <td className="h-[40px] px-16 font-sans text-s14 tabular-nums tracking-givmo text-text-secondary">
                    {account.account_code}
                  </td>
                  <td className="h-[40px] px-16 font-sans text-s14 tracking-givmo text-text">
                    {account.account_name}
                  </td>
                  <td className="h-[40px] px-16 font-sans text-s13 tracking-givmo text-text-secondary">
                    {words.section(account.section, account.account_type)}
                  </td>
                  <td className="h-[40px] px-16 text-right">
                    <BooksLink
                      view={{ page: 'account', accountCode: account.account_code }}
                      className="inline-flex items-center gap-2 font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
                      aria-label={`Activity for ${account.account_name}`}
                    >
                      Activity
                      <Icon icon={ChevronRight} size={13} aria-hidden />
                    </BooksLink>
                  </td>
                </tr>
              ))}
          </tbody>
        ))}
      </table>
    </Card>
  );
}

/** A line's dimensions in words: each axis's served name, and the member it carries. */
export function dimensionWords(words: BooksWords, dimension: BooksDimension[]): string {
  return dimension
    .map((item) => `${words.axis(item.axis_id)}: ${memberWords(words, item.axis_id, item.member)}`)
    .join(' · ');
}

/** An entry's kind (when it reads as anything) and where it came from, in served words. */
export function EntryMeta({
  words,
  kind,
  source,
}: {
  words: BooksWords;
  kind: string;
  source: string | null | undefined;
}) {
  const kindWords = words.entryKind(kind);
  const from = words.source(source);
  const parts: ReactNode[] = [];
  if (kindWords) {
    parts.push(
      <span key="kind" className="rounded-sm bg-neutral-100 px-6 py-2 text-text-secondary">
        {kindWords}
      </span>,
    );
  }
  if (from) parts.push(<span key="source">{from}</span>);
  if (parts.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-8 font-sans text-s12 tracking-givmo text-text-tertiary">
      {parts}
    </span>
  );
}

function Journal({ home, words, walk }: { home: BooksHome; words: BooksWords; walk: BooksWalk }) {
  const journal = useBooksJournal(home.org_ref, walk);
  const { showMore, restarting } = useShowMore(journal);
  if (home.periods.length === 0) {
    return (
      <BooksStateCard
        testId="books-journal-empty"
        title="No entries yet"
        description="The journal lists every entry once the books hold one."
      />
    );
  }
  if (journal.isPending) return <BooksLoading>Loading the journal…</BooksLoading>;
  if (journal.isError && !restarting) {
    return (
      <BooksStateCard
        testId="books-journal-unavailable"
        title="The journal can’t be read right now"
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void journal.refetch()}
            testId="books-journal-retry"
          >
            Try again
          </Button>
        }
      />
    );
  }
  // Restarting a walk whose cursor was refused keeps the pages already shown on screen.
  const pages = journal.data?.pages ?? [];
  const entries = pages.flatMap((page) => page.entries);
  // Served numbers only: each entry's own chain position, and the walk's high-water mark. The
  // engine numbers an organization's entries 1…N with no gaps, so the high-water mark is the
  // number of entries when this walk began.
  const highWater = pages[0]?.high_water ?? 0;
  return (
    <div className="flex flex-col gap-12">
      {entries.length > 0 ? (
        <p
          className="font-sans text-s13 tracking-givmo text-text-secondary"
          data-testid="books-journal-span"
        >
          {WALK_WORDS[walk]}. Entries {count(entries[0]!.chain_position)}–
          {count(entries.at(-1)!.chain_position)} of {count(highWater)}.
        </p>
      ) : null}
      <Card testId="books-journal">
        <table className="w-full border-collapse text-left">
          <colgroup>
            <col />
            <col style={{ width: '160px' }} />
            <col style={{ width: '160px' }} />
          </colgroup>
          <thead className="bg-neutral-50">
            <tr className="border-b border-border-subtle">
              <th className={HEAD}>Entry</th>
              <th className={cn(HEAD, 'text-right')}>Debit</th>
              <th className={cn(HEAD, 'text-right')}>Credit</th>
            </tr>
          </thead>
          {entries.map((entry) => (
            <JournalEntry key={entry.journal_entry_id} entry={entry} words={words} />
          ))}
        </table>
      </Card>
      {journal.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void showMore()}
            disabled={journal.isFetchingNextPage || restarting}
            testId="books-journal-more"
          >
            {journal.isFetchingNextPage || restarting ? 'Loading…' : 'Show more entries'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function JournalEntry({ entry, words }: { entry: BooksJournalEntry; words: BooksWords }) {
  return (
    <tbody
      className="border-b border-border-subtle last:border-b-0"
      data-testid={`books-entry-${entry.chain_position}`}
    >
      <tr>
        <td colSpan={3} className="px-16 pb-4 pt-14">
          <div className="flex flex-wrap items-baseline justify-between gap-8">
            <span className="flex flex-wrap items-baseline gap-12">
              <span className="font-sans text-s13 tabular-nums tracking-givmo text-text-secondary">
                {dateShort(entry.effective_date)}
              </span>
              <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                {entry.memo}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-12">
              <EntryMeta words={words} kind={entry.entry_kind} source={entry.source_event_id} />
              <span className="font-sans text-s12 tabular-nums tracking-givmo text-text-tertiary">
                No. {count(entry.chain_position)}
              </span>
            </span>
          </div>
        </td>
      </tr>
      {entry.lines.map((line) => (
        <tr key={line.line_no}>
          <td className="py-4 pl-32 pr-16 font-sans text-s14 tracking-givmo text-text">
            <span className="mr-12 inline-block w-[40px] tabular-nums text-text-tertiary">
              {line.account_code}
            </span>
            {line.account_name}
            {line.dimension.length > 0 ? (
              <span className="ml-8 font-sans text-s12 tracking-givmo text-text-tertiary">
                {dimensionWords(words, line.dimension)}
              </span>
            ) : null}
          </td>
          <td className="px-16 py-4 text-right font-sans text-s14 tabular-nums tracking-givmo text-text">
            {line.debit_cents ? moneyOrDash(line.debit_cents) : ''}
          </td>
          <td className="px-16 py-4 text-right font-sans text-s14 tabular-nums tracking-givmo text-text">
            {line.credit_cents ? moneyOrDash(line.credit_cents) : ''}
          </td>
        </tr>
      ))}
      <tr aria-hidden>
        <td colSpan={3} className="h-[10px]" />
      </tr>
    </tbody>
  );
}
